import notify from '@ananasbear/notify'
import i18n from '@/plugins/vue-i18n'

const ESIGN_MODAL_SELECTOR = '#eSignReadPrivateKeyModal, #eSignReportModal'
const ESIGN_BODY_CLASS = 'esign-modal-open'

function blurFocusedEsignElement (modal = null) {
  const activeElement = document.activeElement

  if (!(activeElement instanceof HTMLElement)) return

  const focusedModal = modal || activeElement.closest(ESIGN_MODAL_SELECTOR)
  if (!focusedModal || !focusedModal.contains(activeElement)) return

  activeElement.blur()
}

function attachEsignFocusGuard () {
  const jquery = window.jQuery || window.$
  if (typeof jquery !== 'function') return

  jquery(document)
    .off('hide.bs.modal.esignFocusGuard', ESIGN_MODAL_SELECTOR)
    .on('hide.bs.modal.esignFocusGuard', ESIGN_MODAL_SELECTOR, function () {
      blurFocusedEsignElement(this)
    })
}

function detachEsignFocusGuard () {
  const jquery = window.jQuery || window.$
  if (typeof jquery !== 'function') return

  jquery(document)
    .off('hide.bs.modal.esignFocusGuard', ESIGN_MODAL_SELECTOR)
    .off('hidden.bs.modal.esignSession', ESIGN_MODAL_SELECTOR)
}

let activeEsignSession = null

function clearActiveEsignSession () {
  if (!activeEsignSession) return

  window.clearTimeout(activeEsignSession.finishTimer)
  document.removeEventListener('click', activeEsignSession.handleOutsideClick, true)
  document.body.classList.remove(ESIGN_BODY_CLASS)
  detachEsignFocusGuard()
  activeEsignSession = null
}

function finalizeEsignSession (callback, { closeDialog = false } = {}) {
  if (activeEsignSession?.settled) return
  if (!activeEsignSession) {
    callback()
    return
  }

  const session = activeEsignSession
  const jquery = window.jQuery || window.$

  session.settled = true

  const finish = () => {
    if (activeEsignSession !== session) return

    const { returnFocus } = session

    clearActiveEsignSession()
    if (
      returnFocus?.isConnected &&
      returnFocus.getClientRects().length &&
      !returnFocus.closest('[aria-hidden="true"], [inert]')
    ) {
      returnFocus.focus({ preventScroll: true })
    }
    callback()
  }

  if (typeof jquery === 'function') {
    jquery(document).one('hidden.bs.modal.esignSession', ESIGN_MODAL_SELECTOR, finish)
    session.finishTimer = window.setTimeout(finish, 400)
  } else {
    session.finishTimer = window.setTimeout(finish, 0)
  }

  blurFocusedEsignElement()
  if (closeDialog) session.closeDialog?.()
}

const t = key => i18n.t(key)
const requestedProviders = String(process.env.VUE_APP_DOCUMENT_ONLINE_REQUESTED_PROVIDERS || '')
  .split(',')
  .map(provider => provider.trim())
  .filter(Boolean)

let scriptPromise = null
let signerPromise = null

function startEsignSession (reject, closeDialog) {
  clearActiveEsignSession()

  const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null

  const handleOutsideClick = event => {
    const target = event.target

    if (!(target instanceof Element)) return
    if (!target.matches('.modal-backdrop, #eSignReadPrivateKeyModal, #eSignReportModal')) return

    window.setTimeout(() => {
      finalizeEsignSession(() => reject(new Error(t('esign.errors.userCanceled'))))
    }, 0)
  }

  activeEsignSession = {
    reject,
    settled: false,
    returnFocus,
    handleOutsideClick,
    closeDialog
  }

  attachEsignFocusGuard()
  document.body.classList.add(ESIGN_BODY_CLASS)
  document.addEventListener('click', handleOutsideClick, true)
}

function createCallbacks (resolve, reject) {
  return {
    okCallback (data) {
      finalizeEsignSession(() => resolve(data))
    },
    cancelCallback () {
      finalizeEsignSession(() => reject(new Error(t('esign.errors.userCanceled'))), { closeDialog: true })
    },
    errorCallback (error) {
      finalizeEsignSession(() => reject(error), { closeDialog: true })
    }
  }
}

function normalizeEsignError (error, fallbackMessage) {
  if (!error) return new Error(fallbackMessage)
  if (error instanceof Error) return error
  const message = error?.message || error?.error || error?.description || fallbackMessage
  const normalizedError = new Error(String(message))
  normalizedError.cause = error
  return normalizedError
}

const notifyError = (error, fallbackMessage = t('esign.errors.default')) => {
  const normalizedError = normalizeEsignError(error, fallbackMessage)
  notify.error(normalizedError.message)
  return normalizedError
}

const withNotify = async (action, options = {}, fallbackMessage) => {
  try {
    return await action()
  } catch (error) {
    const normalizedError = normalizeEsignError(error, fallbackMessage)
    if (options.notify !== false) notifyError(normalizedError, fallbackMessage)
    throw normalizedError
  }
}

const getPluginScriptSrc = () => {
  const host = String(process.env.VUE_APP_DOCUMENT_ONLINE_PLUGIN_HOST || '').replace(/\/+$/, '')
  const clientId = String(process.env.VUE_APP_DOCUMENT_ONLINE_PLUGIN_CLIENT_ID || '').trim()

  if (!host || !clientId) throw new Error('Plugin host/clientId are not configured in env')

  return `${host}/plugin/v2/${encodeURIComponent(clientId)}`
}

const loadScriptOnce = src => {
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const current = document.querySelector(`script[src="${src}"]`)
    if (current) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(t('esign.errors.loadFailed')))
    document.head.append(script)
  }).catch(error => {
    scriptPromise = null
    throw error
  })

  return scriptPromise
}

const ensureLoaded = async (options = {}) => {
  return withNotify(
    async () => {
      if (window?.eSign) return

      const src = getPluginScriptSrc()
      try {
        await loadScriptOnce(src)

        await new Promise((resolve, reject) => {
          let attempts = 0
          const timer = window.setInterval(() => {
            attempts += 1
            if (window?.eSign) {
              window.clearInterval(timer)
              resolve()
              return
            }
            if (attempts >= 100) {
              window.clearInterval(timer)
              reject(new Error(t('esign.errors.loadFailed')))
            }
          }, 100)
        })
      } catch (error) {
        scriptPromise = null
        document.querySelector(`script[src="${src}"]`)?.remove()
        throw error
      }
    },
    options,
    t('esign.errors.loadFailed')
  )
}

const getSigner = async (options = {}) => {
  return withNotify(
    async () => {
      await ensureLoaded({ notify: options.notify })

      if (signerPromise) return signerPromise

      signerPromise = new Promise((resolve, reject) => {
        const signer = new window.eSign.Signer()
        const signerSettings = new window.eSign.SignerSettings('ua')
        signerSettings.info = console.log
        signerSettings.useOCSP = true
        signerSettings.useTSP = true
        signerSettings.useCadesLong = true
        signerSettings.enableDiviceKeys = true
        signerSettings.providers = requestedProviders.length
          ? requestedProviders
          : ['key', 'depositSign', 'diiaId', 'cloudKey', 'smartId']
        signerSettings.KeyStorageType = 'Session'

        try {
          signer.initialize(signerSettings, () => resolve(signer))
        } catch (error) {
          reject(error)
        }
      }).catch(error => {
        signerPromise = null
        throw error
      })

      return signerPromise
    },
    options,
    t('esign.errors.initFailed')
  )
}

const signItem = async (itemId, settings = {}) => {
  return withNotify(
    async () => {
      const signer = await getSigner({ notify: settings.notify })

      return new Promise((resolve, reject) => {
        const signItemRequest = new window.eSign.SignItemRequest()
        const callbacks = createCallbacks(resolve, reject)
        const bindKey = new window.eSign.BindKey(settings.keyIdentity)

        signItemRequest.errorCallback = callbacks.errorCallback
        signItemRequest.okCallback = callbacks.okCallback
        signItemRequest.cancelCallback = callbacks.cancelCallback
        signItemRequest.displayMessage = settings.displayMessage || 'document'
        signItemRequest.requestedProviders = settings.requestedProviders || requestedProviders
        signItemRequest.useCadesLong = settings.useCadesLong ?? true
        signItemRequest.keyIdentity = settings.keyIdentity || null

        bindKey.required = settings.required ?? true
        if (settings.keyIdentity) signItemRequest.allowToSkipChooseKey = true
        if (settings.availableInn || settings.availableEdrpou) {
          bindKey.expectedEdrpou = settings.availableEdrpou
          bindKey.expectedDrfo = settings.availableInn
        }
        if (settings.keyIdentity) bindKey.identity = settings.keyIdentity

        signItemRequest.keys.push(bindKey)
        startEsignSession(reject, () => signer.closeDialog())
        try {
          signer.signItem(itemId, signItemRequest)
        } catch (error) {
          clearActiveEsignSession()
          reject(error)
        }
      })
    },
    settings,
    t('esign.errors.signItemFailed')
  )
}

const signReport = async (reportId, settings = {}) => {
  return withNotify(
    async () => {
      const signer = await getSigner({ notify: settings.notify })

      return new Promise((resolve, reject) => {
        const signItemRequest = new window.eSign.SignItemRequest('ua')
        const callbacks = createCallbacks(resolve, reject)

        signItemRequest.errorCallback = callbacks.errorCallback
        signItemRequest.okCallback = callbacks.okCallback
        signItemRequest.cancelCallback = callbacks.cancelCallback
        signItemRequest.requestedProviders = settings.requestedProviders || requestedProviders
        signItemRequest.useCadesLong = settings.useCadesLong ?? true
        signItemRequest.keyIdentity = settings.keyIdentity || null
        signItemRequest.keys = []

        if (settings.keys?.length) {
          for (const key of settings.keys) {
            const bindKey = new window.eSign.BindKey()
            bindKey.expectedEdrpou = key.expectedEdrpou
            bindKey.expectedDrfo = key.expectedDrfo
            bindKey.type = key.type
            bindKey.required = key.required
            signItemRequest.keys.push(bindKey)
          }
        }

        startEsignSession(reject, () => signer.closeReportDialog())
        try {
          signer.signReport(reportId, signItemRequest)
        } catch (error) {
          clearActiveEsignSession()
          reject(error)
        }
      })
    },
    settings,
    t('esign.errors.signReportFailed')
  )
}

const forgotPassword = async (provider, options = {}) => {
  return withNotify(
    async () => {
      const signer = await getSigner({ notify: options.notify })
      return signer.forgotPassword(provider)
    },
    options,
    t('esign.errors.resetPasswordFailed')
  )
}

const reset = () => {
  signerPromise = null
}

export default {
  ensureLoaded,
  getSigner,
  notifyError,
  reset,
  signItem,
  signReport,
  forgotPassword
}
